import {useQuery} from "@apollo/client";
import get from "lodash/get";
import {useState} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last30Days} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {GQL_PS_METRICS, PSMetricsResponse} from "@/lib/queries/pagespeed.ts";
import {MetricsHistory} from "@/pages/pagespeed/report/body/history/MetricsHistory.tsx";
import {ReportHistory} from "@/pages/pagespeed/report/body/history/ReportHistory.tsx";
import {LineChart} from "@/pages/pagespeed/report/body/LineChart.tsx";
import {PSErrorState} from "@/pages/pagespeed/report/PSErrorState.tsx";
import {PagespeedLHR} from "@/pages/pagespeed/types.ts";
import {RUMAggregationSelect} from "@/pages/rum/v2/details/RUMAggregationSelect.tsx";
import {RUMAggregationType} from "@/pages/rum/v2/types.ts";

interface HistoryTabProps {
  lhr: PagespeedLHR;
  checkID: number;
}
export const HistoryTab = ({checkID}: HistoryTabProps) => {
  const {user} = useCurrentUserContext();
  const [agg, setAgg] = useState(RUMAggregationType.MED);
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: "history_start",
    toKey: "history_end",
    fixedRangeKey: "history_range",
    withDefault: Last30Days,
  });

  const [metricSelected, setMetricSelected] = useState<string>("tbt");
  const {data, loading, error} = useQuery<PSMetricsResponse>(GQL_PS_METRICS, {
    variables: {
      payload: {
        aggregation: agg,
        check_id: checkID,
        start_date: dateRange.from,
        end_date: dateRange.to,
        metrics: [metricSelected, "performance_score", "best_practices_score"],
      },
    },
    context: {
      noToast: true,
    },
  });
  if (error) {
    return (
      <div>
        <div className="white-block p-3 mb-3">
          <h5>No metrics data found</h5>
          <div className="metrics-graph">
            <PSErrorState error={error.message} />
          </div>
        </div>
        <div className="white-block p-3">
          <h5>Report History</h5>
          <ReportHistory checkID={checkID} user={user!} />
        </div>
      </div>
    );
  }
  if (loading || !data) {
    return <LoadingContainer />;
  }

  const {metrics, series} = data.psMetricHistory;
  const {choices} = data.metricDefs;
  const perfScore = metrics?.find((x) => x.name === "performance_score");
  const bestPracticesScore = metrics?.find((x) => x.name === "best_practices_score");
  const labels = [];
  if (perfScore) {
    labels.push(perfScore);
  }
  if (bestPracticesScore) {
    labels.push(bestPracticesScore);
  }
  const seriesData: unknown[] = [];
  const perfScoreSeries = get(series, "performance_score");
  const bestPracticesScoreSeries = get(series, "best_practices_score");
  if (perfScoreSeries && bestPracticesScoreSeries) {
    seriesData.push(perfScoreSeries);
    seriesData.push(bestPracticesScoreSeries);
  }
  return (
    <div>
      <div className="my-3 w-100 d-flex flex-row-reverse">
        <div className="aggregation-container ms-3 border-3">
          <RUMAggregationSelect
            value={{value: agg}}
            onChange={(aggregationType) => setAgg(aggregationType ? aggregationType.value : RUMAggregationType.MED)}
          />
        </div>
        <DatePicker
          mode={"range"}
          selected={dateRange}
          fixedRange={fixedRangeOption?.value}
          timezone={user!.account!.timezone}
          dateFormat={user!.current_date_format}
          placement={"bottom-end"}
          onChange={(date, fixedRange) => {
            handleRangeChange(date ?? null, fixedRange);
          }}
          disableFutureDates
          hasPopoverInput
        />
      </div>
      <div className="white-block p-3 mb-3">
        <h5>Result Score Time Trend</h5>
        <div className="metrics-graph">
          <LineChart series={seriesData} labels={labels} user={user!} />
        </div>
      </div>
      <div className="white-block p-3 mb-3">
        <MetricsHistory
          metricSelected={metricSelected}
          setMetricSelected={setMetricSelected}
          series={series}
          metrics={metrics}
          choices={choices}
          user={user!}
        />
      </div>
      <div className="white-block p-3">
        <h5>Report History</h5>
        <ReportHistory checkID={checkID} user={user!} />
      </div>
    </div>
  );
};
